import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api, queryKeys } from '@/shared/api';

import type { PaletteInput, PaletteLibrary, UserPalette } from './types';

/** System, personal and workspace palettes of the viewer plus their favorites (one request). */
export function usePaletteLibrary(enabled = true) {
  return useQuery({
    queryKey: queryKeys.palettes.library,
    queryFn: ({ signal }) => api.get<PaletteLibrary>('/v1/color-palettes', { signal }),
    staleTime: 60_000,
    enabled,
  });
}

function useRefresh() {
  const client = useQueryClient();
  return () => client.invalidateQueries({ queryKey: queryKeys.palettes.library });
}

/** A new palette: personal (`workspaceId` null) or shared by that team workspace. */
export function useCreatePalette() {
  const refresh = useRefresh();
  return useMutation({
    mutationFn: ({ workspaceId, ...body }: PaletteInput & { workspaceId: string | null }) =>
      api.post<UserPalette>(
        workspaceId ? `/v1/workspaces/${workspaceId}/color-palettes` : '/v1/color-palettes',
        body,
      ),
    onSettled: () => refresh(),
  });
}

export function useUpdatePalette() {
  const refresh = useRefresh();
  return useMutation({
    mutationFn: ({
      id,
      ...body
    }: { id: string; expected_version?: number } & Partial<PaletteInput>) =>
      api.patch<UserPalette>(`/v1/color-palettes/${id}`, body),
    onSettled: () => refresh(),
  });
}

/** Removes the palette definition only: colors already used by objects stay as they are. */
export function useDeletePalette() {
  const refresh = useRefresh();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/v1/color-palettes/${id}`),
    onSettled: () => refresh(),
  });
}

export function useReorderPalettes() {
  const refresh = useRefresh();
  return useMutation({
    mutationFn: (ids: string[]) => api.put('/v1/color-palettes/order', { ids }),
    onSettled: () => refresh(),
  });
}

export function useSetFavoriteColor() {
  const refresh = useRefresh();
  return useMutation({
    mutationFn: ({ color, favorite }: { color: string; favorite: boolean }) =>
      favorite
        ? api.post('/v1/me/favorite-colors', { color })
        : api.delete(`/v1/me/favorite-colors/${color.replace('#', '')}`),
    onSettled: () => refresh(),
  });
}

export function useSetFavoritePalette() {
  const refresh = useRefresh();
  return useMutation({
    mutationFn: ({ ref, favorite }: { ref: string; favorite: boolean }) =>
      favorite
        ? api.put(`/v1/me/favorite-palettes/${encodeURIComponent(ref)}`)
        : api.delete(`/v1/me/favorite-palettes/${encodeURIComponent(ref)}`),
    onSettled: () => refresh(),
  });
}
